import React from 'react';
import { Link } from 'react-router-dom';
import { FiMapPin, FiUser, FiCamera, FiStar, FiArrowRight } from 'react-icons/fi';
import { FiAirplay } from 'react-icons/fi';
import { motion } from 'framer-motion';
import CardRail, { railCardVariants } from './CardRail';

const ease = [0.22, 1, 0.36, 1];

const services = [
  {
    icon: FiAirplay,
    title: "Airport Transfers",
    description: "Seamless transfers to/from Corfu International Airport with flight tracking and meet & greet service",
    features: ["Flight monitoring", "Meet & greet", "Luggage assistance", "Fixed pricing"],
    popular: true,
    image: "/images/backgrounds/airport-vclass-hero.webp",
    link: "/airport-transfers"
  },
  {
    icon: FiMapPin,
    title: "Port Transfers",
    description: "Reliable transfers to and from Corfu cruise and ferry ports for a smooth arrival or departure",
    features: ["Cruise port pickup", "Ferry terminal", "Flexible scheduling", "Fixed pricing"],
    image: "/images/backgrounds/port-vclass-hero.webp",
    link: "/port-transfers"
  },
  {
    icon: FiUser,
    title: "Private Chauffeur",
    description: "Exclusive personal chauffeur service for daily, weekly, or extended stays, your private driver on demand",
    features: ["Dedicated chauffeur", "Flexible scheduling", "Multi-day packages", "VIP treatment"],
    premium: true,
    image: "/images/backgrounds/chauffeur-liston-hero.webp",
    link: "/hourly-chauffeur"
  },
  {
    icon: FiCamera,
    title: "Sightseeing Tours",
    description: "Discover Corfu's hidden gems with local expert drivers who know every scenic route and secret spot",
    features: ["Local expertise", "Custom itineraries", "Photo stops", "Insider knowledge"],
    image: "/images/backgrounds/tour-vclass-hero.webp",
    link: "/private-tours"
  },
  {
    icon: FiStar,
    title: "Special Events",
    description: "Elegant transportation for weddings, celebrations, and special occasions across Corfu",
    features: ["Event coordination", "Multiple vehicles", "Decorations available", "Professional service"],
    image: "/images/backgrounds/coastal-drive-vclass.webp",
    link: "/wedding-transfers"
  }
];

const Services = () => {
  return (
    <section id="services" className="relative py-24 lg:py-32 overflow-hidden" style={{ backgroundColor: '#faf7f6' }}>
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease }}
          className="text-center mb-16"
        >
          <p className="text-[#ba1609] text-xs uppercase tracking-[0.4em] mb-4">What We Offer</p>
          <h2 className="text-3xl lg:text-5xl font-light text-[#1a1012]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Our <span className="italic text-[#ba1609]">Services</span>
          </h2>
        </motion.div>

        <CardRail label="services">
          {services.map((service) => (
            <motion.div key={service.title} variants={railCardVariants}>
              <Link to={service.link} className="block h-full">
                <div className="group relative bg-white border border-black/10 rounded-2xl overflow-hidden hover:border-[#ba1609]/30 transition-[border-color,box-shadow] duration-500 h-full flex flex-col shadow-sm hover:shadow-xl hover:shadow-black/5">
                  {/* Inserted photo */}
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                    {service.popular && (
                      <span className="absolute top-4 left-4 text-[10px] bg-[#ba1609] text-white px-3 py-1 rounded-full uppercase tracking-wider">
                        Most Popular
                      </span>
                    )}
                    {service.premium && (
                      <span className="absolute top-4 left-4 text-[10px] bg-[#1a1012] text-white px-3 py-1 rounded-full uppercase tracking-wider">
                        Premium
                      </span>
                    )}
                  </div>

                  {/* Icon chip at the photo/content boundary (outside the photo's overflow-hidden so it is not clipped) */}
                  <div className="absolute right-6 top-44 -translate-y-1/2 z-10 w-12 h-12 rounded-xl bg-white shadow-lg shadow-black/10 border border-black/5 flex items-center justify-center">
                    <service.icon className="w-5 h-5 text-[#ba1609]" />
                  </div>

                  {/* Content */}
                  <div className="p-6 pt-8 sm:p-7 sm:pt-8 flex flex-col flex-grow">
                    <h3 className="text-[#1a1012] text-lg font-medium mb-3">{service.title}</h3>
                    <p className="text-[#5b4d4f] text-sm leading-relaxed font-light mb-6 flex-grow">{service.description}</p>

                    <ul className="space-y-2 mb-6">
                      {service.features.map((feature) => (
                        <li key={feature} className="flex items-center text-[#5b4d4f] text-xs">
                          <div className="w-1 h-1 bg-[#ba1609] rounded-full mr-2.5 shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <div className="mt-auto pt-4 border-t border-black/10 flex items-center justify-between">
                      <span className="text-[#ba1609] text-sm font-light">Learn More</span>
                      <FiArrowRight className="w-4 h-4 text-[#5b4d4f] group-hover:text-[#ba1609] group-hover:translate-x-1 transition-all duration-300" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </CardRail>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="text-center"
        >
          <Link
            to="/booking"
            className="inline-block bg-[#ba1609] text-white px-10 py-4 rounded-full font-medium hover:bg-[#8e1107] transition-all duration-300 text-base tracking-wide"
          >
            Book Your Transfer
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
